import React, { useState } from "react";
import { FieldArray } from "formik";
import { FieldDefinition } from "../../../types/formMaps";
import { FiChevronsDown, FiChevronsUp, FiHelpCircle } from "react-icons/fi";
import PlainTextDisplay from "./PlainTextDisplay";
import Tooltip from "../../Tooltip";

interface DataGroupProps {
  fieldDef: FieldDefinition;
  values: any;
}

const DataGroup: React.FC<DataGroupProps> = ({ fieldDef, values }) => {
  const [showAllRows, setShowAllRows] = useState(false);

  const toggleShowAllRows = () => {
    setShowAllRows(!showAllRows);
  };
  return (
    <FieldArray name={fieldDef.name}>
      {({ push, remove }) => {
        let valArray = values[fieldDef.name] ?? [];
        if (!valArray.length) valArray = [valArray];
        const displayedRows = showAllRows ? valArray : valArray.slice(0, 2);

        return (
          <div className="p-4">
            <label className="text-black text-lg font-semibold">
              {fieldDef.label}
            </label>
            {/* Add a container with overflow properties */}
            <div className="overflow-x-auto">
              <table className="outline outline-1 border outline-gray-200 m-1 overflow-hidden rounded-lg table-fixed">
                {/* Add colgroup to set fixed widths */}
                <colgroup>
                  {fieldDef.subfields?.map((subfield) => (
                    <col
                      key={subfield.name}
                      style={{
                        width: `${
                          subfield.type === "currency" ? "120px" : "180px"
                        }`,
                      }}
                    />
                  ))}
                </colgroup>
                <thead>
                  <tr className="border-b border-gray-200">
                    {fieldDef.subfields?.map((subfield) => (
                      <th
                        key={subfield.name}
                        className="px-3 py-2 text-left whitespace-nowrap truncate"
                      >
                        {subfield.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {!displayedRows?.length && (
                    <tr>
                      <td colSpan={fieldDef.subfields?.length}>
                        <div className="text-gray-500 text-center py-4">
                          No data
                        </div>
                      </td>
                    </tr>
                  )}
                  {displayedRows?.map((item, index) => (
                    <tr key={index}>
                      {fieldDef.subfields?.map((subfield) => (
                        <td
                          key={subfield.name}
                          className="px-3 py-2 relative text-gray-700"
                        >
                          <PlainTextDisplay
                            value={valArray[index][subfield.name]}
                            fieldType={subfield.type}
                            maxLength={80}
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                  {valArray?.length > 2 && (
                    <button
                      type="button"
                      className="p-2 text-blue-500 cursor-pointer"
                      onClick={toggleShowAllRows}
                    >
                      <div className="flex items-center">
                        {showAllRows ? <FiChevronsUp /> : <FiChevronsDown />}
                        {showAllRows ? "Show less" : "Show all"}
                      </div>
                    </button>
                  )}
                  {displayedRows?.length > 0 && (
                    <tr className="bg-gray-100">
                      {fieldDef.subfields?.map(
                        (subfield: FieldDefinition, index: number) => (
                          <>
                            <td
                              key={index}
                              className={`px-3 py-2 whitespace-nowrap relative ${
                                subfield.type === "currency" ? "text-end" : ""
                              }`}
                            >
                              {!!subfield.totalTooltipText && (
                                <Tooltip content={subfield.totalTooltipText}>
                                  <>
                                    {!!subfield.calcTotal && (
                                      <>{subfield.calcTotal(values)}</>
                                    )}

                                    <span className="text-xs text-blue-900 absolute bottom-1 right-1">
                                      <FiHelpCircle />
                                    </span>
                                  </>
                                </Tooltip>
                              )}
                              {index === 0 && <>Total</>}
                              {!!subfield.calcTotal &&
                                !subfield.totalTooltipText && (
                                  <>{subfield.calcTotal(values)}</>
                                )}
                            </td>
                          </>
                        )
                      )}
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        );
      }}
    </FieldArray>
  );
};

export default DataGroup;
